import type { TranscriptSegment } from 'src/meetings/detail/tabs/transcript/types';

// The backend's merge pipeline renames every unresolved diarisation label to a
// friendly "Unknown speaker N" (see UnknownSpeakerPresentationHeuristic), so
// that is the shape the UI actually receives. Raw "SPEAKER_n" is matched too as
// a safety net in case a transcript slips through without the presentation step.
const UNIDENTIFIED_SPEAKER_PATTERN = /^(SPEAKER_\d+|Unknown speaker \d+)$/i;

type UnidentifiedSpeaker = {
  label: string;
  statementCount: number;
};

const isUnidentifiedSpeaker = (speaker: string): boolean =>
  UNIDENTIFIED_SPEAKER_PATTERN.test(speaker.trim());

/**
 * A diarised transcript labels each segment with a placeholder speaker label
 * ("Unknown speaker 1", or a raw "SPEAKER_0") until someone is identified. This
 * groups those still-unidentified labels and counts their statements, in order
 * of first appearance in the transcript.
 */
export const getUnidentifiedSpeakers = (
  segments: TranscriptSegment[] | undefined,
): UnidentifiedSpeaker[] => {
  if (!segments) return [];

  const countByLabel = new Map<string, number>();
  for (const segment of segments) {
    if (!isUnidentifiedSpeaker(segment.speaker)) continue;
    countByLabel.set(segment.speaker, (countByLabel.get(segment.speaker) ?? 0) + 1);
  }

  return [...countByLabel.entries()].map(([label, statementCount]) => ({ label, statementCount }));
};

/**
 * Turns a placeholder diarisation label into a friendlier display name, e.g.
 * "SPEAKER_0" becomes "Speaker 1" and "Unknown speaker 2" becomes "Speaker 2".
 * Anything else is shown as-is.
 */
export const formatSpeakerLabel = (label: string): string => {
  const rawSpeakerMatch = label.match(/^SPEAKER_(\d+)$/);
  if (rawSpeakerMatch) return `Speaker ${Number(rawSpeakerMatch[1]) + 1}`;

  const unknownSpeakerMatch = label.match(/^Unknown speaker (\d+)$/i);
  if (unknownSpeakerMatch) return `Speaker ${Number(unknownSpeakerMatch[1])}`;

  return label;
};
